import type { ToolCallLike } from '@/lib/assistant-tool-metadata'

export interface OpsDigestAction {
	label: string
	path: string
}

export interface OpsDigestItem {
	priority: number
	category: string
	title: string
	detail: string
	site_id?: string
	site_name?: string
	store_id?: number
	navigate?: OpsDigestAction
}

export interface OpsDigestData {
	summary: string
	metrics: {
		stockout_count: number
		overdue_restock_count: number
		failed_payments_7d: number
		revenue_change_pct: number | null
		recent_revenue_dollars?: number
		prior_revenue_dollars?: number
	}
	items: OpsDigestItem[]
	actions: OpsDigestAction[]
	currency?: string
}

function parseToolJson(result: string | undefined): Record<string, unknown> | null {
	if (!result) return null
	try {
		const parsed = JSON.parse(result)
		return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
	} catch {
		return null
	}
}

export function extractOpsDigestFromToolCalls(
	toolCalls: ToolCallLike[] | undefined,
): OpsDigestData | null {
	if (!toolCalls?.length) return null

	for (const tc of toolCalls) {
		if (tc.status !== 'done' || tc.name !== 'get_ops_digest' || !tc.result) continue
		const parsed = parseToolJson(tc.result)
		if (!parsed?.__ops_digest) continue

		const metrics = parsed.metrics as Record<string, unknown> | undefined
		const items = Array.isArray(parsed.items)
			? (parsed.items as OpsDigestItem[])
			: []
		const actions = Array.isArray(parsed.actions)
			? (parsed.actions as OpsDigestAction[])
			: items
				.map((item) => item.navigate)
				.filter((nav): nav is OpsDigestAction => Boolean(nav))

		return {
			summary: typeof parsed.summary === 'string' ? parsed.summary : 'Ops digest',
			items,
			actions,
			currency: typeof parsed.currency === 'string' ? parsed.currency : undefined,
			metrics: {
				stockout_count: Number(metrics?.stockout_count ?? 0),
				overdue_restock_count: Number(metrics?.overdue_restock_count ?? 0),
				failed_payments_7d: Number(metrics?.failed_payments_7d ?? 0),
				revenue_change_pct: metrics?.revenue_change_pct != null
					? Number(metrics.revenue_change_pct)
					: null,
				recent_revenue_dollars: metrics?.recent_revenue_dollars != null
					? Number(metrics.recent_revenue_dollars)
					: undefined,
				prior_revenue_dollars: metrics?.prior_revenue_dollars != null
					? Number(metrics.prior_revenue_dollars)
					: undefined,
			},
		}
	}

	return null
}

export function extractOpsDigestActionsFromToolCalls(
	toolCalls: ToolCallLike[] | undefined,
): OpsDigestAction[] {
	return extractOpsDigestFromToolCalls(toolCalls)?.actions ?? []
}
